import React from "react";
import { Package } from "lucide-react";
import { Card, CardContent } from "src/components/ui/Card";
import { Input } from "src/components/ui/Input";
import { Label } from "src/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/Select";
import { SessionSchedulingType } from "src/services/sessionsService";
import { SchedulingPurchasedPlan } from "src/services/adminSchedulingService";

export interface BatchCurriculumUnitOption {
  unit_id: string;
  name: string;
  subjectName?: string | null;
}

interface SessionModeAndPlanCardProps {
  schedulingType: SessionSchedulingType;
  onSchedulingTypeChange: (type: SessionSchedulingType) => void;
  batchId: string;
  onBatchChange: (batchId: string) => void;
  purchasedPlans: SchedulingPurchasedPlan[];
  isPlansLoading: boolean;
  hasPlansError: boolean;
  plansError: unknown;
  selectedPlan: SchedulingPurchasedPlan | undefined;
  unitId: string;
  onUnitChange: (unitId: string) => void;
  batchUnits: BatchCurriculumUnitOption[];
  isBatchUnitsLoading: boolean;
}

export const SessionModeAndPlanCard: React.FC<SessionModeAndPlanCardProps> = ({
  schedulingType,
  onSchedulingTypeChange,
  batchId,
  onBatchChange,
  purchasedPlans,
  isPlansLoading,
  hasPlansError,
  plansError,
  selectedPlan,
  unitId,
  onUnitChange,
  batchUnits,
  isBatchUnitsLoading,
}) => {
  return (
    <Card className="border border-slate-200 rounded-xl shadow-xs overflow-hidden bg-white">
      <div className="bg-[#faf9fe] px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="text-xs font-mono text-purple-700 font-medium">STEP 1 • CADENCE & BATCH ALLOCATION</div>
          <h2 className="text-base font-bold text-slate-900">Session Mode & Purchased Plan</h2>
        </div>
      </div>

      <CardContent className="p-6 space-y-5">
        {/* SCHEDULING TYPE TOGGLE */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
            Scheduling Type
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div
              onClick={() => onSchedulingTypeChange("on_demand")}
              className={`relative flex items-start gap-3 p-3.5 rounded-lg border-2 cursor-pointer transition ${
                schedulingType === "on_demand"
                  ? "border-purple-600 bg-purple-50/40"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <input
                type="radio"
                name="schedulingType"
                checked={schedulingType === "on_demand"}
                onChange={() => onSchedulingTypeChange("on_demand")}
                className="mt-0.5 text-purple-600 focus:ring-purple-500"
              />
              <div>
                <div className="flex items-center gap-1.5 font-bold text-sm text-slate-900">
                  <span>On-Demand Session</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                    Single 1:1 or Group
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Creates a single scheduled meeting session linked to an enrollment or stand-alone batch.
                </p>
              </div>
            </div>

            <div
              onClick={() => onSchedulingTypeChange("recurring")}
              className={`relative flex items-start gap-3 p-3.5 rounded-lg border-2 cursor-pointer transition ${
                schedulingType === "recurring"
                  ? "border-purple-600 bg-purple-50/40"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <input
                type="radio"
                name="schedulingType"
                checked={schedulingType === "recurring"}
                onChange={() => onSchedulingTypeChange("recurring")}
                className="mt-0.5 text-purple-600 focus:ring-purple-500"
              />
              <div>
                <div className="flex items-center gap-1.5 font-bold text-sm text-slate-900">
                  <span>Recurring Series</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                    Weekly Cadence
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Generates recurring multi-week sessions utilizing purchased plan capacity.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* PURCHASED PLAN / BATCH SELECT */}
        <div className="space-y-1.5">
          <Label htmlFor="batch" className="text-xs font-semibold text-slate-700">
            {schedulingType === "recurring" ? "Purchased Plan / Batch (Required)" : "Batch (Optional)"}
          </Label>
          <div className="relative">
            <Package className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Select
              value={batchId || "none"}
              onValueChange={onBatchChange}
              disabled={isPlansLoading}
            >
              <SelectTrigger className="pl-10 h-10 text-xs font-medium rounded-lg border border-slate-300 bg-white">
                <SelectValue
                  placeholder={
                    isPlansLoading ? "Loading purchased plans..." : "Select purchased plan"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {schedulingType === "on_demand" && (
                  <SelectItem value="none">No batch (Standalone session)</SelectItem>
                )}
                {purchasedPlans.map((plan: SchedulingPurchasedPlan) => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.name} • {plan.studentName} • {plan.remaining_sessions ?? 0}/{plan.total_sessions ?? 0} sessions remaining
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedPlan && (
            <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
              <span>
                Product: <strong className="text-slate-700">{selectedPlan.product_name}</strong>
                {selectedPlan.tutorName ? ` • Tutor: ${selectedPlan.tutorName}` : ""}
              </span>
              <span className="font-mono text-purple-700 font-semibold">
                Capacity: {selectedPlan.remaining_sessions ?? 0} left
              </span>
            </div>
          )}
          {hasPlansError && (
            <p className="text-xs text-rose-600">
              Unable to load purchased plans: {plansError instanceof Error ? plansError.message : "Please try again."}
            </p>
          )}
        </div>

        {/* CURRICULUM UNIT ALIGNMENT */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="subject" className="text-xs font-semibold text-slate-700">
              Curriculum Scope / Subject
            </Label>
            <Input
              id="subject"
              placeholder="Select a batch to populate subject"
              value={selectedPlan?.subjectName || "IB Physics DP • Grade 11"}
              readOnly
              disabled
              className="h-9 text-xs bg-slate-50 border-slate-200 text-slate-600 cursor-not-allowed"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="curriculumUnit" className="text-xs font-semibold text-slate-700">
              Target Curriculum Unit
            </Label>
            <Select
              value={unitId || "none"}
              onValueChange={onUnitChange}
              disabled={isBatchUnitsLoading || !batchId}
            >
              <SelectTrigger className="h-9 text-xs font-medium rounded-lg border-slate-300 bg-white">
                <SelectValue
                  placeholder={isBatchUnitsLoading ? "Loading units..." : "Select unit (Optional)"}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No specific unit (Open diagnostic)</SelectItem>
                {batchUnits.map((unit) => (
                  <SelectItem key={unit.unit_id} value={unit.unit_id}>
                    {unit.subjectName ? `${unit.subjectName}: ` : ""}
                    {unit.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

